import { Stack, Box, SimpleGrid, Heading, Text, Input, Select, Checkbox, Alert, Table, Tbody, Tr, Td } from '@chakra-ui/react'
import RadioHeader from '../../includes/RadioHeader'
import RichText from '../../includes/RichText'

export default function Settings ({ stage, availableToSites, setAvailableToSites, enabled, setEnabled, enableAfter, setEnableAfter, isRecurring, recurringFrequency, setRecurringFrequency, recurringDuration, setRecurringDuration, adHoc, setAdHoc, postSubmissionText, setPostSubmissionText, trial, sites, stages, isPoll, showPostSubmissionEditor, setShowPostSubmissionEditor, setIsRecurring }) {
  function selectSchedule (val) {
    if (val === 'none') {
      setIsRecurring(false)
      setAdHoc(false)
      setRecurringFrequency(0)
      setRecurringDuration(0)
    }
    if (val === 'recur') {
      setIsRecurring(true)
      setAdHoc(false)
    }
    if (val === 'adHoc') {
      setIsRecurring(false)
      setAdHoc(true)
      setRecurringFrequency(0)
      setRecurringDuration(0)
    }
  }

  function updateEnableAfter (data) {
    if (data.delay) {
      data.delay = parseInt(data.delay)
    }
    if (data.stage) {
      if (data.stage.indexOf(':') > -1) {
        const components = data.stage.split(':')
        data.stage = components[0]
        data.stageRecurrence = parseInt(components[1])
      } else {
        data.stageRecurrence = null
      }
    }
    const newEnableAfter = Object.assign({}, enableAfter, data)
    setEnableAfter(newEnableAfter)
  }

  return (
    <Box>
      <Box p={3} mt={15} borderWidth='1px' rounded='md' bg='gray.50'>
        <Heading as='h3' size='md'>Who has access to this stage</Heading>
        <Text color='gray.500' mb={5}>Specify the sites with access to this stage. For example, you might use different languages for different sites.</Text>
        <SimpleGrid columns={2} spacing={3}>
          <Box rounded='md' shadow='sm' bg='white' p={3}>
            <RadioHeader
              checked={!availableToSites} name='availableToSites' value='none' title='Available to all participants'
              onChange={e => setAvailableToSites(null)}
              content={
                <>
                  <Text color='gray.500' mb={5}>This stage will be shown to all participants in the trial, subject to the settings below.</Text>
                </>
              }
            />
          </Box>
          <Box rounded='md' shadow='sm' bg='white' p={3}>
            <RadioHeader
              checked={availableToSites} name='availableToSites' value='none' title='Available to specific sites only'
              onChange={e => { if (!availableToSites) setAvailableToSites([]) }}
              content={
                <>
                  <Text color='gray.500' mb={5}>This stage will only be shown to the sites selected below.</Text>
                  {availableToSites &&
                    <Table size='xs'>
                      <Tbody>
                        {sites.map(site =>
                          <Tr key={site._id}>
                            <Td>
                              <Checkbox
                                isChecked={availableToSites.indexOf(site._id) > -1}
                                onChange={e => {
                                  const index = availableToSites.indexOf(site._id)
                                  const newSites = Object.assign([], availableToSites)
                                  if (index === -1) newSites.push(site._id)
                                  else newSites.splice(index, 1)
                                  setAvailableToSites(newSites)
                                }}
                              />
                            </Td>
                            <Td>{site.name}</Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>}
                </>
              }
            />
          </Box>
        </SimpleGrid>
      </Box>

      <Box p={3} mt={15} borderWidth='1px' rounded='md' bg='gray.50'>
        <Heading as='h3' size='md'>When to enable your stage</Heading>
        <Text color='gray.500' mb={5}>Choose when your stage will become available to participants during the trial.</Text>

        {isPoll &&
          <Alert status='info' variant='left-accent' mb={5}>
            Since poll mode is enabled for this stage, viewers with the link to the poll will be able to view and submit responses to the poll at any time. The settings below only apply to full trial participants using the participant portal or mobile apps.
          </Alert>}

        <SimpleGrid columns={3} spacing={3}>
          <Box rounded='md' shadow='sm' bg='white' p={3}>
            <RadioHeader
              checked={!enabled && !enableAfter} name='enable' value='none' title='Not available to participants'
              onChange={e => { setEnabled(false); setEnableAfter(null) }}
              content={
                <>
                  <Text color='gray.500' mb={5}>This stage is not available to participants. Select this option if, for example, staff will complete this data on behalf of the participants.</Text>
                </>
              }
            />
          </Box>

          <Box rounded='md' shadow='sm' bg='white' p={3}>
            <RadioHeader
              checked={enabled && !enableAfter} name='enable' value='true' title='Available to participants'
              onChange={e => { setEnabled(true); setEnableAfter(null) }}
              content={
                <>
                  <Text color='gray.500' mb={5}>The stage is available for completion by participants. This is useful if you want participants to be able to access this stage from "Day 0" or for controlled availability.</Text>
                </>
              }
            />
          </Box>

          <Box rounded='md' shadow='sm' bg='white' p={3}>
            <RadioHeader
              checked={!!enableAfter} name='enable' value='after' title='Make available after...'
              onChange={e => { setEnabled(false); setEnableAfter({ stage: null, delay: 0 }) }}
              content={
                <>
                  <Text color='gray.500' mb={5}>Enable this stage for participants after another stage has been completed. Please note that this means that this stage might become available to each participant on different dates, depending on when they completed the other stages.</Text>
                  {!!enableAfter &&
                    <Stack direction='column'>
                      <Stack direction='row' alignItems='center' justifyContent='center'>
                        <Text fontSize='sm'>Enable</Text>
                        <Input size='xs' placeholder='2' bg='white' w='50px' value={enableAfter?.delay ?? ''} onChange={e => updateEnableAfter({ delay: e.target.value })} />
                        <Text fontSize='sm'>days after</Text>
                      </Stack>
                      <Text fontSize='sm'>the paticipant completes</Text>
                      <Select
                        size='xs' bg='white' placeholder='Select another stage' w='100%'
                        value={`${enableAfter?.stage}${enableAfter?.stageRecurrence != null ? `:${enableAfter?.stageRecurrence}` : ''}`}
                        onChange={e => updateEnableAfter({ stage: e.target.value })}
                      >
                        {stages?.filter(s => s._id !== stage?._id).map(s => {
                          return s.isRecurring
                            ? Array.from({ length: s.recurringDuration }, (_, i) => i).map(recurrence =>
                              <option value={`${s._id}:${recurrence}`} key={`${s._id}:${recurrence + 1}`}>{s.name} ({recurrence + 1})</option>)
                            : <option value={s._id} key={s._id}>{s.name}</option>
                        })}
                      </Select>
                    </Stack>}
                </>
              }
            />
          </Box>
        </SimpleGrid>
      </Box>

      <Box p={3} mt={15} borderWidth='1px' rounded='md' bg='gray.50'>
        <Heading as='h3' size='md'>Schedule your stage</Heading>
        <Text color='gray.500' mb={5}>Choose how participants will interact with your stage.</Text>

        <SimpleGrid columns={3} spacing={3}>
          <Box rounded='md' shadow='sm' bg='white' p={3}>
            <RadioHeader
              checked={!isRecurring && !adHoc} name='schedule' value='none' title='This stage will not repeat'
              onChange={e => selectSchedule(e.target.value)}
              content={
                <>
                  <Text color='gray.500' mb={5}>The stage can only be completed once.</Text>
                </>
            }
            />
          </Box>

          <Box rounded='md' shadow='sm' bg='white' p={3}>
            <RadioHeader
              checked={isRecurring} name='schedule' value='recur' title='This stage will repeat'
              onChange={e => selectSchedule(e.target.value)}
              content={
                <Box>
                  <Text color='gray.500' mb={5}>The trial will dynamically create versions of this stage based on your rules.</Text>
                  {isRecurring &&
                    <>
                      <Stack spacing={2} direction='row' justifyContent='center' alignItems='center'>
                        <Text fontSize='sm'>Repeat every</Text>
                        <Input size='xs' bg='white' w='50px' isDisabled={!isRecurring} placeholder='Days' type='number' value={recurringFrequency ?? ''} onChange={e => setRecurringFrequency(e.target.value)} />
                        <Text fontSize='sm'>days</Text>
                      </Stack>
                      <Stack mt={1} spacing={2} direction='row' justifyContent='center' alignItems='center'>
                        <Text fontSize='sm'>for</Text>
                        <Input size='xs' bg='white' w='50px' isDisabled={!isRecurring} placeholder='Number' type='number' value={recurringDuration ?? ''} onChange={e => setRecurringDuration(e.target.value)} />
                        <Text fontSize='sm'>occurrences</Text>
                      </Stack>
                    </>}
                </Box>
              }
            />
          </Box>

          <Box rounded='md' shadow='sm' bg='white' p={3}>
            <RadioHeader
              checked={adHoc} name='schedule' value='adHoc' title='Ad-hoc input'
              onChange={e => selectSchedule(e.target.value)}
              content={
                <>
                  <Text color='gray.500' mb={5}>The trial will allow participants to complete this stage on an ad-hoc basis. This means that participants can repeatedly complete this stage at any time.</Text>
                </>
              }
            />
          </Box>
        </SimpleGrid>
      </Box>

      {showPostSubmissionEditor && // Delay until stage is loaded
        <Box p={3} mt={15} borderWidth='1px' rounded='md' bg='gray.50'>
          <Heading as='h3' size='md'>Post-submission text</Heading>
          <Text color='gray.500' mb={5}>This text will be displayed to participants after they complete this stage.</Text>
          <RichText value={postSubmissionText ?? ''} onChange={e => setPostSubmissionText(e)} forType='trial' forId={trial?._id} allowImages />
        </Box>}
    </Box>
  )
}
